import type { PlansOrBillingIntent, SubscriptionPromptModel } from './index';
import { log } from './log';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { DefaultError } from '@tanstack/query-core';
import type { SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import type { AnalyticsService, PaywallTrigger } from '@thinkalpha/ui-ioc/core/types/AnalyticsService';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import type { CurrentUserModel } from 'src/models/CurrentUserModel';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getPlanChangeSubscription, isActiveSubscriptionStatus } from 'src/models/SubscriptionPlanCatalogModel/rules';
import { getUserSecurityPackageQuery } from 'src/queries/user';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

type SecurityPackageQuery = QueryObserverModel<
    z.infer<typeof SecurityPackage>,
    DefaultError,
    z.infer<typeof SecurityPackage>
>;

@injectable()
export class SubscriptionPromptModelImpl implements SubscriptionPromptModel {
    readonly #disposableStack = new DisposableStack();

    #open = signal(false);
    #billingOpen = signal(false);
    #billingPlanChangeSubscriptionId = signal<string | null>(null);
    #billingPlanChangeImmediatePreferred = signal(false);
    #autoShown = false;
    #securityPackageQuery: SecurityPackageQuery;
    readonly #analyticsService: AnalyticsService | undefined;

    readonly loading: ReadonlySignal<boolean>;
    readonly subscriptionSettingsIntentReady: ReadonlySignal<boolean>;
    readonly isSubscriptionUser: ReadonlySignal<boolean>;
    readonly needsSubscription: ReadonlySignal<boolean>;

    readonly open: ReadonlySignal<boolean> = this.#open;
    readonly billingOpen: ReadonlySignal<boolean> = this.#billingOpen;
    readonly isAnyModalOpen: ReadonlySignal<boolean> = computed(() => this.#open.value || this.#billingOpen.value);
    readonly billingPlanChangeSubscriptionId: ReadonlySignal<string | null> = this.#billingPlanChangeSubscriptionId;
    readonly billingPlanChangeImmediatePreferred: ReadonlySignal<boolean> = this.#billingPlanChangeImmediatePreferred;
    readonly shouldShowBillingAndPlan: ReadonlySignal<boolean>;
    readonly shouldShowSubscribe: ReadonlySignal<boolean>;

    constructor(
        @inject('QueryObserverModelFactory')
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('CurrentUserModel') currentUserModel: CurrentUserModel,
        @inject('AnalyticsService') @optional() analyticsService?: AnalyticsService,
    ) {
        this.#analyticsService = analyticsService;
        this.#securityPackageQuery = queryObserverModelFactory(getUserSecurityPackageQuery('me'));
        this.#disposableStack.use(this.#securityPackageQuery);
        this.loading = computed(() => this.#securityPackageQuery.signal.value.isFetching);
        this.subscriptionSettingsIntentReady = computed(() => {
            const secPkgResult = this.#securityPackageQuery.signal.value;
            return (
                (secPkgResult.data !== undefined || secPkgResult.status === 'error') &&
                currentUserModel.isSubscriptionUserReady.value
            );
        });
        this.isSubscriptionUser = currentUserModel.isSubscriptionUser;
        this.needsSubscription = computed(() => {
            const secPkgResult = this.#securityPackageQuery.signal.value;
            const secPkg = secPkgResult.data;

            if (TRACE) {
                log.trace('needsSubscription evaluating:', {
                    secPkgStatus: secPkgResult.status,
                    secPkgError: secPkgResult.error?.message,
                    secPkgData: secPkg
                        ? {
                              roles: secPkg.roles.length,
                              accessList: secPkg.accessList.map((access) => access.type),
                              subscriptions: secPkg.subscriptions.length,
                          }
                        : undefined,
                });
            }

            if (!secPkg) return false;

            const hasActiveSubscription = secPkg.subscriptions.some((subscription) =>
                isActiveSubscriptionStatus(subscription.status),
            );
            const hasRoles = secPkg.roles.length > 0;

            if (TRACE) {
                log.trace('needsSubscription result:', {
                    isSubscriptionUser: this.isSubscriptionUser.value,
                    hasActiveSubscription,
                    hasRoles,
                    result: this.isSubscriptionUser.value && !hasActiveSubscription && !hasRoles,
                });
            }

            return this.isSubscriptionUser.value && !hasActiveSubscription && !hasRoles;
        });
        this.shouldShowBillingAndPlan = computed(
            () =>
                this.subscriptionSettingsIntentReady.value &&
                this.isSubscriptionUser.value &&
                !this.needsSubscription.value,
        );
        this.shouldShowSubscribe = computed(
            () => this.subscriptionSettingsIntentReady.value && this.needsSubscription.value,
        );

        // Auto-open the modal when needsSubscription becomes true (first time only)
        this.#disposableStack.defer(
            effect(() => {
                const needs = this.needsSubscription.value;
                if (TRACE) {
                    log.trace('auto-open effect:', {
                        needsSubscription: needs,
                        autoShown: this.#autoShown,
                    });
                }
                if (needs && !this.#autoShown) {
                    this.#autoShown = true;
                    this.#openSetup('auto');
                    if (TRACE) {
                        log.trace('auto-opening modal');
                    }
                }
            }),
        );
    }

    openSubscriptionSetup(trigger: PaywallTrigger): void {
        if (TRACE) {
            log.trace(`openSubscriptionSetup called from ${trigger}`);
        }
        this.#openSetup(trigger);
    }

    /** Every way the setup modal opens goes through here, so each opening is tracked once */
    #openSetup(trigger: PaywallTrigger): void {
        const alreadyOpen = this.#open.peek();
        this.#billingPlanChangeSubscriptionId.value = null;
        this.#billingPlanChangeImmediatePreferred.value = false;
        this.#open.value = true;
        if (!alreadyOpen) {
            this.#analyticsService?.track({ type: 'paywall_viewed', trigger });
        }
    }

    closeSubscriptionSetup(): void {
        if (TRACE) {
            log.trace('closeSubscriptionSetup called');
        }
        this.#open.value = false;
    }

    openPlansOrBilling(trigger: PaywallTrigger, intent: PlansOrBillingIntent = 'root'): boolean {
        const secPkgResult = this.#securityPackageQuery.signal.value;
        const status = secPkgResult.status;
        const hasSecurityPackage = secPkgResult.data !== undefined;

        if (!this.subscriptionSettingsIntentReady.value) {
            log.info('Open plans or billing deferred until security package is ready', {
                intent,
                queryStatus: status,
                isFetching: secPkgResult.isFetching,
            });
            return false;
        }

        if (!hasSecurityPackage) {
            log.warn('Open plans or billing consumed without opening because security package failed to load', {
                intent,
                queryStatus: status,
                isFetching: secPkgResult.isFetching,
                error: secPkgResult.error instanceof Error ? secPkgResult.error.message : secPkgResult.error,
            });
            return true;
        }

        if (!this.isSubscriptionUser.value) {
            log.info('Open plans or billing consumed without opening for non-subscription user', {
                intent,
                queryStatus: status,
                accessListTypes: secPkgResult.data.accessList.map((a) => a.type),
                roleCount: secPkgResult.data.roles.length,
                subscriptionCount: secPkgResult.data.subscriptions.length,
            });
            return true;
        }

        if (this.needsSubscription.value) {
            log.info('Open plans or billing opening subscription setup modal', {
                intent,
                queryStatus: status,
                subscriptionCount: secPkgResult.data.subscriptions.length,
            });
            this.#openSetup(trigger);
            return true;
        }

        const planChangeSubscription =
            intent === 'select-plan' ? getPlanChangeSubscription(secPkgResult.data.subscriptions) : undefined;
        this.#billingPlanChangeSubscriptionId.value = planChangeSubscription?.id ?? null;
        this.#billingPlanChangeImmediatePreferred.value = planChangeSubscription !== undefined;
        this.#billingOpen.value = true;

        log.info('Open plans or billing opening billing modal', {
            intent,
            queryStatus: status,
            subscriptionCount: secPkgResult.data.subscriptions.length,
            planChangeSubscriptionId: planChangeSubscription?.id ?? null,
            planChangeImmediatePreferred: planChangeSubscription !== undefined,
        });
        return true;
    }

    closeBilling(): void {
        if (TRACE) {
            log.trace('closeBilling called');
        }
        this.#billingPlanChangeSubscriptionId.value = null;
        this.#billingPlanChangeImmediatePreferred.value = false;
        this.#billingOpen.value = false;
    }

    consumeBillingPlanChangeRequest(): void {
        this.#billingPlanChangeSubscriptionId.value = null;
        this.#billingPlanChangeImmediatePreferred.value = false;
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
